/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { FeatureData } from 'src/core/types/telemetryTypes';
import { FileView } from 'src/customText/appShell/dataTaggingPage/components/FilesViewDropdown';
import { ModelGuidanceCardKey } from 'src/modules/modelGuidance/components/tabs/OverviewTab';
import { TranslationCreationSteps } from 'src/translation/components/TranslationCreationPage';
import { PalettePivotType } from 'src/verticals/EntityPalette';

export const featuresDataForTelemetry: FeatureData[] = [
    { title: 'Extract PII', featureName: 'extractPii', serviceName: 'extractPii' },
    { title: 'Key Phrases', featureName: 'extractKP', serviceName: 'extractKP' },
    { title: 'Linked Entities', featureName: 'extractLE', serviceName: 'extractLE' },
    { title: 'Entity Extraction', featureName: 'extractedNE', serviceName: 'extractedNE' },
    { title: 'Health information', featureName: 'extractTA4H​', serviceName: 'extractTA4H​' },
    { title: 'Analyze Sentiment', featureName: 'analyzeSentiment', serviceName: 'analyzeSentiment' },
    { title: 'Language Detection', featureName: 'detectLanguage', serviceName: 'detectLanguage' },
    { title: 'Question answering', featureName: 'answerQuestions', serviceName: 'answerQuestions' },
    { title: 'customSummarization', featureName: 'customSummarization', serviceName: 'customSummarization' },
    { title: 'Summarization information', featureName: 'summerizeDocuments', serviceName: 'summerizeDocuments' },
    { title: 'Summarization conversation', featureName: 'summarizeConversations', serviceName: 'summarizeConversations' },
    { title: 'Chat GPT - Expedia demo', featureName: 'ChatGPT', serviceName: 'ChatGPT' },
    { title: 'Translate text', featureName: 'translateText', serviceName: 'translateText' },
    { title: 'Generate questions', featureName: 'qnAGenerator', serviceName: 'qnAGenerator' },
    { title: 'translation', featureName: 'customTranslation', serviceName: 'customTranslation' },
    { title: 'document translation', featureName: 'documentTranslation', serviceName: 'documentTranslation' },
    { title: 'document translation demo', featureName: 'documentTranslationDemo', serviceName: 'documentTranslationDemo' },
    { title: 'Call center', featureName: 'callCenter', serviceName: 'callCenter' },
    { title: 'clu', featureName: 'CustomCLU', serviceName: 'CLU' },
    { title: 'orchestration', featureName: 'CustomOrchestration', serviceName: 'Orchestration' },
    { title: 'ner', featureName: 'CustomNER', serviceName: 'NER' },
    { title: 'classification', featureName: 'CustomClassification', serviceName: 'Classification' },
    { title: 'qnaMaker', featureName: 'CustomQA', serviceName: 'QA' },
    { title: 'landing page', featureName: 'LandingPage', serviceName: 'LandingPage' },
    { title: 'root', featureName: 'LandingPage', serviceName: 'LandingPage' },
    { title: 'luis', featureName: 'LUIS', serviceName: 'luis' },
    { title: '', featureName: '', serviceName: '' }
];

export const getFeatureNameFromTitle = (title: string) =>
    featuresDataForTelemetry.find(feature => feature.title === title)?.featureName ?? '';

export const getServiceNameFromTitle = (title: string) =>
    featuresDataForTelemetry.find(feature => feature.title === title)?.serviceName ?? '';

export const pivotKeyToPivotTitle = (key: PalettePivotType) => {
    switch (key) {
        case 'tags':
            return 'Labels';
        case 'distribution':
            return 'Distribution';
        case 'recommendations':
            return 'Recommendations';
        default:
            return '';
    }
};

export const fileViewToOptionName = (option: FileView) => (option === 'all' ? 'All' : 'Single');

export const modelGuidancePivotKeyToName = (key: string) => {
    switch (key) {
        case 'overview-tab':
            return 'Overview';
        case 'evaluation-tab':
            return 'EntityPerfomance';
        case 'test-set-tab':
            return 'Testset';
        case 'data-distribution-tab':
            return 'Distribution';
        case 'confusion-matrix-tab':
            return 'ConfusionMatrix';
        default:
            return '';
    }
};

export const modelGuidanceCardKeyToName = (key: ModelGuidanceCardKey) => {
    switch (key) {
        case 'not-enough-data-card':
            return 'GuidanceTrainset';
        case 'missing-labels-card':
            return 'GuidanceTestset';
        case 'unbalanced-labels-card':
            return 'Balanced';
        case 'uneven-distribution-card':
            return 'Distribution';
        case 'confusion-labels-card':
            return 'GuidanceConfusion';
        default:
            return '';
    }
};

export type DTPageName = 'jobCreation' | 'jobHistory' | 'settings';

export type DTJobCreationStepName =
    | 'initialConfig'
    | 'basicInfo'
    | 'filesSelection'
    | 'filesDestination'
    | 'additionalOptions'
    | 'reviewFinish';

export const getDTActiveStepName = (activeStep: TranslationCreationSteps): DTJobCreationStepName => {
    switch (activeStep) {
        case 0:
            return 'initialConfig';
        case 1:
            return 'basicInfo';
        case 2:
            return 'filesSelection';
        case 3:
            return 'filesDestination';
        case 4:
            return 'additionalOptions';
        case 5:
            return 'reviewFinish';
        default:
            null;
    }
};
